import { useState } from 'react'
import { useUrlManager } from './useUrlManager'

interface UseModalParamOptions {
  /**
   * Keep the value in the page URL (`?key=value`), so a refresh or a shared
   * link reopens the modal. Off for a host that is itself an overlay the URL
   * can't restore — the value then lives in local state.
   */
  syncWithUrl?: boolean
}

export interface ModalParam {
  /** What the modal is open for (e.g. a player id), or null while closed. */
  value: string | null
  open: (value: string) => void
  close: () => void
}

/**
 * Which item a modal is open for, held in one place: the URL query param when
 * `syncWithUrl`, local state otherwise. Both writes REPLACE the history entry,
 * so opening never strands an extra Back step.
 */
export const useModalParam = (
  key: string,
  { syncWithUrl = true }: UseModalParamOptions = {}
): ModalParam => {
  'use memo'

  const { currentQueryParams, setParams } = useUrlManager()
  const [localValue, setLocalValue] = useState<string | null>(null)

  if (!syncWithUrl) {
    return {
      value: localValue,
      open: (value) => setLocalValue(value),
      close: () => setLocalValue(null),
    }
  }

  return {
    value: currentQueryParams.get(key) || null,
    open: (value) => setParams({ [key]: value }),
    close: () => setParams({ [key]: null }),
  }
}
